import { useMutation } from '@apollo/client';
import {
  CheckoutAppEnum,
  CheckoutOriginPlatform,
  ICheckoutCart,
  ICheckoutProductError,
  ICreateCartQueryResult,
  ICreateCartQueryResultData,
  IShoppingCartProduct,
  SourceEnum,
} from '@monofront/types';
import { CREATE_CHECKOUT_CART } from '../../queries/checkout';
import { IAuthState } from '../../contexts';
import { useSubscribeInCheckout } from './useSubscribeInCheckout';
import { useCallback } from 'react';

export interface ICartAttributes {
  isProspect?: boolean;
  source?: SourceEnum;
  checkoutOrigin: CheckoutAppEnum;
  checkoutOriginPlatform: CheckoutOriginPlatform;
}

export interface ISynchronizeCart {
  cart?: ICheckoutCart;
  errors?: ICheckoutProductError[];
}

interface IUseCreateCheckoutFromShoppingCart {
  synchronizeCarts: (
    products: IShoppingCartProduct[],
    validate: boolean,
    attributes: ICartAttributes,
  ) => Promise<ISynchronizeCart>;
  forceSynchronizeCarts: (
    products: IShoppingCartProduct[],
    attributes: ICartAttributes,
  ) => Promise<ISynchronizeCart>;
  data?: ICreateCartQueryResultData;
  loading: boolean;
  error: any;
}

/**
 This hook is meant to synchronize the firebase cart with the OMS cart
 @returns IUseCreateCheckoutFromShoppingCart
 */
export const useCreateCheckoutFromShoppingCart = (
  useAuthentication: () => IAuthState,
): IUseCreateCheckoutFromShoppingCart => {
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);

  const [createCart, { data, loading, error }] =
    useMutation<ICreateCartQueryResult>(CREATE_CHECKOUT_CART, {
      context: {
        Headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      },
    });

  const synchronizeCarts = useCallback(
    async (
      products: IShoppingCartProduct[],
      validate = true,
      attributes: ICartAttributes,
    ) => {
      const res = await createCart({
        variables: {
          cart: {
            items: products.map(({ sku, quantity }) => ({ sku, quantity })),
          },
          attributes: { ...attributes, isProspect: isSubscriptionAvailable },
          validate,
        },
      });

      return {
        cart: res?.data?.createCart?.cart,
        errors: res?.data?.createCart?.errors,
      };
    },
    [createCart, isSubscriptionAvailable],
  );

  const forceSynchronizeCarts = useCallback(
    async (products: IShoppingCartProduct[], attributes: ICartAttributes) =>
      synchronizeCarts(
        products,
        false, // validate
        attributes,
      ),
    [synchronizeCarts],
  );

  return {
    synchronizeCarts,
    forceSynchronizeCarts,
    data: data?.createCart ?? undefined,
    loading,
    error,
  };
};

export default useCreateCheckoutFromShoppingCart;
